import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Users, FileText, Receipt, Mail, Rocket, ChevronRight, ChevronLeft } from "lucide-react";

const STORAGE_KEY = "fz_onboarding_done_v1";

const STEPS = [
  {
    icon: Rocket,
    title: "Benvenuto in FreelanZen! 🌱",
    body: "In 5 tappe da 30 secondi ti mostro le funzioni chiave. Puoi saltare o tornare quando vuoi — hai comunque 30 giorni di prova gratuita.",
  },
  {
    icon: Users,
    title: "1. I tuoi clienti",
    body: "Aggiungi i tuoi clienti manualmente o importa un file CSV/Excel. Puoi salvare P.IVA, codice fiscale, PEC e codice destinatario SdI per la fatturazione elettronica.",
    to: "/clienti",
    cta: "Vai ai Clienti",
  },
  {
    icon: FileText,
    title: "2. Preventivi con firma digitale",
    body: "Crea preventivi PDF con il tuo logo e invia il link al cliente: firma online → fattura generata automaticamente. Niente più email di follow-up.",
    to: "/preventivi",
    cta: "Crea un preventivo",
  },
  {
    icon: Receipt,
    title: "3. Fatture e SdI",
    body: "Emetti fatture con PDF branded. Un click per scaricare l'XML FatturaPA da caricare sul tuo sistema di trasmissione SdI. Solleciti AI generati con Claude Sonnet 4.5.",
    to: "/fatture",
    cta: "Vai alle Fatture",
  },
  {
    icon: Mail,
    title: "4. Inbox Email + WhatsApp",
    body: "Collega la tua casella email (Gmail, Outlook, Aruba…) e/o WhatsApp Business — rispondi ai clienti direttamente da FreelanZen, con template Meta approvati.",
    to: "/inbox",
    cta: "Apri Inbox",
  },
  {
    icon: Sparkles,
    title: "🎁 Un piccolo regalo",
    body: "Per ogni 3 amici freelance che porti su FreelanZen ricevi 1 mese gratis. Il tuo codice referral è già attivo — condividilo dalla sezione Invita.",
    to: "/invita",
    cta: "Vai a Invita amici",
  },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Show only for local (non-google, since Google users typically have existing habits)
    // Trigger conditions: user is on trial, hasn't completed onboarding
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Also skip if user has clients already (returning user via re-registration)
    (async () => {
      try {
        const { data } = await api.get("/clients");
        if (Array.isArray(data) && data.length === 0) {
          // Fresh account with no clients yet — show tour
          setTimeout(() => setOpen(true), 800);
        }
      } catch { /* silent */ }
    })();
  }, [user]);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ done: true, at: new Date().toISOString() }));
    setOpen(false);
  };

  const goAndFinish = (to) => {
    finish();
    if (to) navigate(to);
  };

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6"
        data-testid="onboarding-dialog"
      >
        <DialogTitle className="sr-only">{s.title}</DialogTitle>
        <DialogDescription className="sr-only">{s.body}</DialogDescription>
        <div className="text-center py-2 sm:py-4">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-zen-primary/10 text-zen-primary flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Icon size={26}/>
          </div>
          <h2 className="font-heading text-xl sm:text-2xl mb-2">{s.title}</h2>
          <p className="text-zen-ink2 leading-relaxed text-sm max-w-md mx-auto">{s.body}</p>
        </div>
        <div className="flex justify-center gap-1.5 my-2" data-testid="onboarding-dots">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-zen-primary" : "w-1.5 bg-zen-border"}`}
            />
          ))}
        </div>
        {/* Footer: stack on mobile, side-by-side on ≥sm. Buttons wrap gracefully. */}
        <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex flex-wrap gap-2 order-2 sm:order-1 justify-center sm:justify-start">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                data-testid="onboarding-prev"
                className="rounded-full h-9 px-4 text-sm"
              >
                <ChevronLeft size={14} className="mr-1"/> Indietro
              </Button>
            )}
            {s.to && (
              <Button
                variant="outline"
                onClick={() => goAndFinish(s.to)}
                data-testid="onboarding-cta"
                className="rounded-full h-9 px-4 text-sm border-zen-primary/30 text-zen-primary hover:bg-zen-primary/5"
              >
                {s.cta}
              </Button>
            )}
            <Button
              onClick={() => (isLast ? finish() : setStep(step + 1))}
              data-testid="onboarding-next"
              className="rounded-full h-9 px-4 text-sm bg-zen-primary hover:bg-zen-primaryHover"
            >
              {isLast ? "Iniziamo!" : "Avanti"}
              {!isLast && <ChevronRight size={14} className="ml-1"/>}
            </Button>
          </div>
          <Button
            variant="ghost"
            data-testid="onboarding-skip"
            onClick={finish}
            className="text-zen-ink2 text-sm h-8 px-2 order-1 sm:order-2 self-center sm:self-auto"
          >
            Salta il tour
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
